import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Select } from '@creditornot/cb-select';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification } from 'common/components';
import { Typography } from 'common/typography';
import { localize } from 'common/utils';
import { SharedMenu } from 'modules/shared-menu/types';
import { isNewSelection, isSharedMenuBootstrapping } from 'modules/shared-menu/utils';

type Props = {
  isLoading: boolean;
  error: any;
  sharedMenus: SharedMenu[];
  onContinue: (sharedMenuId: string) => void;
  onClose: () => void;
};

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`;

const EmptyContainer = styled.div``;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
`;

const ModalButtons = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

export const SelectMenu = ({ isLoading, error, sharedMenus, onContinue, onClose }: Props) => {
  const intl = useIntl();
  const [selectedSharedMenuId, setSelectedSharedMenuId] = useState<string | null>(null);

  const sharedMenuOptions: { label: string; value: string }[] = useMemo(() => {
    return (
      sharedMenus
        ?.filter(sharedMenu => !isSharedMenuBootstrapping(sharedMenu.draft) && !isNewSelection(sharedMenu))
        .map(sharedMenu => ({
          label: localize(sharedMenu.name, sharedMenu.primary_language),
          value: sharedMenu.id,
        })) ?? []
    );
  }, [sharedMenus]);

  return (
    <>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="shared-menu.link.venues-selected.label" />
      </ModalHeader>
      <StyledModalContent>
        <TextContainer>
          <Typography.Body3>
            <FormattedMessage id="shared-menu.link.venues-selected.modal-description" />
          </Typography.Body3>
        </TextContainer>
        <EmptyContainer>
          <FormRow
            labelAlign="center"
            horizontalPadding
            label={intl.formatMessage({ id: 'shared-menu.link.venues-selected.select-menu' })}
          >
            <Select value={selectedSharedMenuId} options={sharedMenuOptions} onChange={setSelectedSharedMenuId} />
          </FormRow>
        </EmptyContainer>
      </StyledModalContent>
      <StyledModalFooter>
        {error && <APIErrorNotification error={error} style={{ marginBlockEnd: '16px', width: '100%' }} />}
        <ModalButtons>
          <Button variant="lightBlue" size="small" style={{ marginInlineEnd: '8px' }} onClick={onClose}>
            <FormattedMessage id="state.cancel" />
          </Button>
          <Button
            variant="blue"
            size="small"
            loading={isLoading}
            disabled={isLoading || !selectedSharedMenuId}
            onClick={() => onContinue(selectedSharedMenuId!)}
          >
            <FormattedMessage id="state.continue" />
          </Button>
        </ModalButtons>
      </StyledModalFooter>
    </>
  );
};
